import React from 'react';
import { Box, Paper, Typography, Button, Chip, List, ListItem, ListItemIcon, ListItemText, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarIcon from '@mui/icons-material/Star';
import { useTranslation } from 'react-i18next';

const BRAND = '#427898';
const BRAND_LIGHT = '#eef4f8';

const PricingCard = ({ plan, onSelect, loading }) => {
    const { t } = useTranslation();
    const h = plan.highlighted;

    const [whole, decimal] = plan.isFree
        ? ['0', '00']
        : String(plan.price).split('.');

    return (
        <Paper
            elevation={0}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                width: '100%',
                borderRadius: '16px',
                border: h ? `2px solid ${BRAND}` : '1px solid #e2e8f0',
                overflow: 'hidden',
                transition: 'box-shadow 0.2s, transform 0.2s',
                boxShadow: h ? '0 8px 32px rgba(66,120,152,0.18)' : '0 2px 8px rgba(0,0,0,0.06)',
                '&:hover': {
                    boxShadow: h ? '0 12px 40px rgba(66,120,152,0.24)' : '0 6px 20px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                },
            }}
        >
            {/* Colored top accent */}
            <Box sx={{ height: 5, backgroundColor: h ? BRAND : '#e2e8f0' }} />

            <Box sx={{ p: 3.5, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>

                {/* Plan name + popular badge */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="overline" sx={{ color: BRAND, fontWeight: 700, letterSpacing: 1.2, lineHeight: 1 }}>
                        {plan.name}
                    </Typography>
                    {h && (
                        <Chip
                            icon={<StarIcon sx={{ fontSize: '13px !important', color: 'white !important' }} />}
                            label={t('pricing.popular')}
                            size="small"
                            sx={{ backgroundColor: BRAND, color: 'white', fontWeight: 600, fontSize: 10, height: 20, ml: 'auto' }}
                        />
                    )}
                </Box>

                {/* Description */}
                {plan.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
                        {plan.description}
                    </Typography>
                )}

                {/* Price block */}
                <Box sx={{ mb: 3 }}>
                    {plan.isFree ? (
                        <Typography variant="h3" fontWeight={800} color={BRAND} sx={{ lineHeight: 1 }}>
                            {t('pricing.free')}
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.secondary', mt: 0.8 }}>
                                {plan.currency}
                            </Typography>
                            <Typography variant="h3" fontWeight={800} color={BRAND} sx={{ lineHeight: 1 }}>
                                {whole}
                            </Typography>
                            <Box sx={{ mt: 0.8 }}>
                                <Typography sx={{ fontSize: 15, fontWeight: 600, color: BRAND, lineHeight: 1 }}>
                                    .{decimal || '00'}
                                </Typography>
                                {plan.interval && (
                                    <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.2 }}>
                                        / {plan.interval}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Marketing features */}
                {plan.marketingFeatures?.length > 0 && (
                    <Box sx={{ mb: 3, flexGrow: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1 }}>
                            {t('pricing.includes')}
                        </Typography>
                        <List dense disablePadding>
                            {plan.marketingFeatures.map((feat, i) => {
                                const label = typeof feat === 'string' ? feat : feat?.name ?? '';
                                return (
                                    <ListItem key={i} disableGutters sx={{ py: 0.4 }}>
                                        <ListItemIcon sx={{ minWidth: 26 }}>
                                            <CheckCircleOutlineIcon sx={{ fontSize: 17, color: BRAND }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={label}
                                            primaryTypographyProps={{ variant: 'body2', color: 'text.primary', lineHeight: 1.5 }}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>
                )}

                {/* CTA */}
                <Box sx={{ mt: 'auto' }}>
                    <Button
                        fullWidth
                        variant={h ? 'contained' : 'outlined'}
                        disabled={loading}
                        onClick={() => onSelect(plan)}
                        sx={{
                            borderRadius: '10px',
                            py: 1.3,
                            fontWeight: 700,
                            fontSize: 14,
                            textTransform: 'none',
                            letterSpacing: 0.3,
                            ...(h
                                ? { backgroundColor: BRAND, '&:hover': { backgroundColor: '#356580' } }
                                : { borderColor: BRAND, color: BRAND, borderWidth: '1.5px', '&:hover': { borderWidth: '1.5px', backgroundColor: BRAND_LIGHT } }
                            ),
                        }}
                    >
                        {loading
                            ? <CircularProgress size={20} sx={{ color: h ? 'white' : BRAND }} />
                            : plan.isFree ? t('pricing.getStarted') : t('pricing.choosePlan')
                        }
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default PricingCard;
